// Temporary Account Strategist calendar swaps. While a swap is active, every
// scheduling link for that strategist (Have Questions? calendar, Onboarding
// Complete pop-up, strategy-call banner, Revive tour) points to the temporary
// HubSpot link instead. Swaps start and end on their own based on the date in
// Eastern time, so no deploy is needed to revert.
//
// To add a swap, append an entry; remove expired entries when convenient.

import type { ClientOwner } from './clientOwners';

export interface StrategistCalendarSwap {
  ownerId: ClientOwner['id'];
  /** First day the temporary link is used, YYYY-MM-DD in America/New_York. */
  startDate: string;
  /** Last day the temporary link is used (inclusive), YYYY-MM-DD in America/New_York. */
  endDate: string;
  url: string;
}

export const STRATEGIST_CALENDAR_SWAPS: StrategistCalendarSwap[] = [
  {
    ownerId: 'Ben',
    startDate: '2026-10-01',
    endDate: '2026-11-01',
    url: 'https://meetings.hubspot.com/ben1778/nov-speculoai-onboarding-meeting-ben',
  },
];

const SWAP_TIME_ZONE = 'America/New_York';

/** Calendar date of `now` in Eastern time as YYYY-MM-DD. */
const toEasternDate = (now: Date): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: SWAP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);

export type StrategistCalendarLinkKind = 'booking' | 'strategy';

/**
 * The strategist's scheduling link for `now`: the temporary link while a swap
 * covers today's Eastern date, otherwise the strategist's own booking or
 * strategy-meeting link.
 */
export const getStrategistCalendarLink = (
  strategist: ClientOwner,
  kind: StrategistCalendarLinkKind,
  now: Date = new Date(),
  swaps: StrategistCalendarSwap[] = STRATEGIST_CALENDAR_SWAPS
): string => {
  const today = toEasternDate(now);
  const swap = swaps.find(
    (s) => s.ownerId === strategist.id && s.startDate <= today && today <= s.endDate
  );
  if (swap) return swap.url;
  return kind === 'booking' ? strategist.bookingUrl : strategist.strategyMeetingUrl;
};

/** The strategist with both scheduling links resolved for `now`. */
export const withStrategistCalendarLinks = (
  strategist: ClientOwner,
  now: Date = new Date(),
  swaps: StrategistCalendarSwap[] = STRATEGIST_CALENDAR_SWAPS
): ClientOwner => ({
  ...strategist,
  bookingUrl: getStrategistCalendarLink(strategist, 'booking', now, swaps),
  strategyMeetingUrl: getStrategistCalendarLink(strategist, 'strategy', now, swaps),
});
